import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BackgroundMedia from "@/components/BackgroundMedia";
import DisciplineGallery from "@/components/DisciplineGallery";
import Seo from "@/components/Seo";
import { Container, SectionLabel, ButtonLux, Reveal } from "@/components/primitives";
import { api } from "@/lib/api";

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const FALLBACK_DISCIPLINES = [
  { title: "Bespoke Furniture & Design Solutions", body: "Pieces designed by our studio and crafted through the atelier — furniture, joinery, partition systems and structural details, tailored to the scale and rhythm of each space.", images: [] },
  { title: "Curated Brands", body: "A small, considered roster of Indian and international partners we bring into projects when the discipline calls for a catalogue product resolved in a spatial context.", images: [] },
  { title: "Art & Accessories", body: "Original works sourced and commissioned for the room — photography, painting, sculpture and textile — alongside the objects that carry a life inside them.", images: [] },
  { title: "Styling", body: "The final quiet layer. Textiles, books, ceramics, greenery and the small choices that let a designed room settle into an inhabited one.", images: [] },
  { title: "Decorative Lighting", body: "Statement fixtures and considered ambient layers — decorative lighting positioned as part of the composition rather than a fitting on a plan.", images: [] },
  { title: "Gifting", body: "Client-considered gifting for practices, projects and moments — curated, personalised and sent from our atelier to yours.", images: [] },
];
const FALLBACK_SLUGS = ["bespoke", "curated-brands", "art-accessories", "styling", "decorative-lighting", "gifting"];

export default function Bespoke() {
  const [params] = useSearchParams();
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.getPage("bespoke").then(setDoc).catch(() => {}); }, []);

  const content = useMemo(() => doc?.content || {}, [doc]);
  const hero = content.hero || {};
  const ctaBlock = content.cta || {};
  const eyebrow = hero.eyebrow || "THE WORK";
  const headline = hero.headline || "The work that brings\na space together.";
  const intro = hero.intro || "From bespoke furniture and design solutions to curated brands, art, styling, decorative lighting and gifting — our work is about bringing the right elements together to complete a space.";
  const heroImage = hero.image_url || "";
  const heroVideo = hero.video_url || "";
  const hasBackdrop = !!(heroImage || heroVideo);

  const disciplines = useMemo(() => {
    const cmsItems = Array.isArray(content.disciplines?.items) ? content.disciplines.items : [];
    const source = cmsItems.length > 0 ? cmsItems : FALLBACK_DISCIPLINES;
    return source.map((d, i) => ({
      title: d?.title || FALLBACK_DISCIPLINES[i]?.title || `Discipline ${i + 1}`,
      body: d?.body || FALLBACK_DISCIPLINES[i]?.body || "",
      images: Array.isArray(d?.images) ? d.images.filter((u) => typeof u === "string" && u.trim()) : [],
      slug: (d?.slug && String(d.slug).trim()) || FALLBACK_SLUGS[i] || slugify(d?.title || ""),
      cta_label: typeof d?.cta_label === "string" ? d.cta_label : "",
      cta_href: typeof d?.cta_href === "string" ? d.cta_href : "",
      cta_visible: d?.cta_visible !== false,
    }));
  }, [content]);

  useEffect(() => {
    const target = (params.get("section") || "").trim().toLowerCase() || window.location.hash.replace(/^#/, "").trim().toLowerCase();
    if (!target || disciplines.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) { el.style.scrollMarginTop = "90px"; el.scrollIntoView({ behavior: "smooth", block: "start" }); }
    }, 350);
    return () => clearTimeout(t);
  }, [params, disciplines.length]);

  const ctaLabel = ctaBlock.label || "Start a Project Conversation";
  const ctaHref = ctaBlock.href || "/contact";
  const ml = (t) => t.split("\n").map((line, i) => <span key={i} className="block">{line}</span>);

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo title="The Work · Onedot6" description={intro} path="/bespoke" />
      <TopBar />
      {hasBackdrop ? (
        <div className="relative min-h-[520px] md:min-h-[640px] flex items-end overflow-hidden">
          <BackgroundMedia videoUrl={heroVideo} imageUrl={heroImage} testId="work-hero-media" />
          <div className="absolute inset-0 bg-gradient-to-b from-lux-ink/55 via-lux-ink/25 to-lux-ink/75 pointer-events-none" />
          <Container className="relative pt-40 pb-12 md:pb-20">
            <SectionLabel light>{eyebrow}</SectionLabel>
            <h1 className="font-serif text-white font-light tracking-tight leading-none mt-2 text-5xl md:text-7xl lg:text-8xl">{ml(headline)}</h1>
            <p className="text-white/85 font-light leading-relaxed mt-5 text-base md:text-lg max-w-[720px]">{intro}</p>
          </Container>
        </div>
      ) : (
        <Container className="pt-32 md:pt-40">
          <SectionLabel>{eyebrow}</SectionLabel>
          <h1 className="font-serif text-lux-ink font-light tracking-tight leading-none mt-2 text-5xl md:text-7xl lg:text-8xl">{ml(headline)}</h1>
          <p className="text-lux-sec font-light leading-relaxed mt-5 text-base md:text-lg max-w-[720px]">{intro}</p>
        </Container>
      )}

      <Container className="pt-16 md:pt-24 pb-24">
        {disciplines.map((d, i) => (
          <Reveal key={d.slug || i}>
            <div id={d.slug} data-testid={`discipline-${i}`} className={`border-t border-lux-line pt-8 mb-16 md:mb-24 flex flex-col ${i % 2 === 1 ? "md:flex-row-reverse" : "md:flex-row"} gap-6 md:gap-14 items-stretch`}>
              <div className="md:flex-[1.3] w-full">
                <DisciplineGallery images={d.images} testId={`discipline-gallery-${i}`} />
              </div>
              <div className="flex-1 flex flex-col justify-center">
                <p className="text-[11px] tracking-[0.25em] uppercase text-lux-accent mb-2.5" data-testid={`discipline-idx-${i}`}>0{i + 1} · Discipline</p>
                <h2 className="font-serif text-lux-ink font-light tracking-tight leading-tight text-2xl md:text-4xl mb-3.5">{d.title}</h2>
                <p className="text-lux-sec font-light leading-relaxed text-[15px] md:text-[17px] max-w-[560px]">{d.body}</p>
                {d.cta_visible && (d.cta_label || d.slug === "gifting") && (
                  <div className="mt-5">
                    <ButtonLux label={d.cta_label || "Enter Gifting →"} variant="outline" to={d.cta_href || (d.slug === "gifting" ? "/gifting" : "/contact")} testId={`discipline-${d.slug}-cta`} />
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        ))}
        <div className="border-t border-lux-line pt-8 flex flex-col gap-3">
          <ButtonLux label="View All Work →" variant="outline" to="/projects" testId="view-all-work" />
          <p className="text-xs text-lux-muted italic">Explore how these disciplines come together across our completed projects.</p>
        </div>
        <div className="mt-14">
          <ButtonLux label={ctaLabel} to={ctaHref} testId="work-cta" />
        </div>
      </Container>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
